import './style.scss';

import { Checkbox, Chip, FormControlLabel } from '@mui/material';
import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import AsyncCreatableSelect from 'react-select/async-creatable';
import { Button, Modal } from 'reactstrap';
import { v4 as uuidv4 } from 'uuid';

import MediaCropper from '@/components/cropper';
import Dropzone from '@/components/dropzone';
import ImageImportModal from '@/components/image-import-modal';
import { Constants } from '@/constants';
import Vitola from '@/models/Vitola';
import { uploadToS3 } from '@/utils/imageUtils';

// Define default vitolas and helper function as before
export const defaultVitolas = [
  { label: 'Churchill', value: 'Churchill' },
  { label: 'Cigarillo', value: 'Cigarillo' },
  { label: 'Corona', value: 'Corona' },
  { label: 'Double Corona', value: 'Double Corona' },
  { label: 'Double Toro', value: 'Double Toro' },
  { label: 'Lancero', value: 'Lancero' },
  { label: 'Perfecto', value: 'Perfecto' },
  { label: 'Robusto', value: 'Robusto' },
  { label: 'Toro', value: 'Toro' },
  { label: 'Torpedo', value: 'Torpedo' },
];

export const formattedVitolaName = ({
  alias,
  shape,
  dimen,
}: {
  alias: string;
  shape: string;
  dimen: string;
}) => {
  let name = '';
  if (alias && shape) {
    // Alias (Shape - Size)
    name = `${alias} (${shape} - ${dimen})`;
  } else if (alias && !shape) {
    // Alias (Size)
    name = `${alias} (${dimen})`;
  } else if (!alias && shape) {
    // Shape (Size)
    name = `${shape} (${dimen})`;
  } else {
    // Just size
    name = dimen;
  }
  return name;
};

// Initialize arrays (only once)
const lengths: (number | string)[] = [];
const gauges: (number | string)[] = [];

if (lengths.length === 0) {
  for (let l = 4.0; l < 8.0; l += 0.5) {
    lengths.push(l);
  }
  lengths.push('Other');
}

if (gauges.length === 0) {
  for (let g = 34; g < 71; g++) {
    gauges.push(g);
  }
  gauges.push('Other');
}

interface VitolaEditorProps {
  open: boolean;
  vitola?: Vitola;
  onClose: () => void;
  onSave?: (vitola: Vitola) => void;
}

const VitolaEditor: React.FC<VitolaEditorProps> = (props) => {
  const [alias, setAlias] = useState('');
  const [shape, setShape] = useState<any>(null);
  const [length, setLength] = useState<string | number | null>(null);
  const [otherLength, setOtherLength] = useState<string>('');
  const [gauge, setGauge] = useState<string | number | null>(null);
  const [otherGauge, setOtherGauge] = useState<string>('');
  const [boxPressed, setBoxPressed] = useState(false);
  const [tubo, setTubo] = useState(false);
  const [showMediaCropper, setShowMediaCropper] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showImageUrlModal, setShowImageUrlModal] = useState(false);

  const vitolaImage = useRef(null);

  useEffect(() => {
    const { vitola } = props;
    if (vitola) {
      setAlias(vitola.alias);
      if (vitola.shape) {
        setShape({ label: vitola.shape, value: vitola.shape });
      }
      if (vitola.dimen) {
        const parts = vitola.dimen.split('"x');
        setLength(parseFloat(parts[0], 10));
        setGauge(parseInt(parts[1], 10));
        // FIXME If either is not a chip default, set other
      }
      if (vitola.image_url) {
        setSelectedImage(vitola.image_url);
      } else {
        setSelectedImage(null);
      }
    }
  }, [props.vitola]);

  const loadShapes = (
    searchTerm: string,
    callback: (options: any[]) => void
  ) => {
    console.log(`${Constants.clientPath}/cigars/shapes?q=${searchTerm}`);
    return axios
      .get(`${Constants.clientPath}/cigars/shapes?q=${searchTerm}`)
      .then((res) => {
        console.log(res.data);
        callback(
          res.data.map((s: string) => ({
            value: s,
            label: s,
          }))
        );
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const validate = () => {
    // eslint-disable-next-line
    let valid = true;
    // console.log('Shape:', shape);
    // console.log('Length:', length);
    // console.log('Gauge:', gauge);
    // TODO Include validation? Admins shouldn't need this but not a bad idea to include
    // if (!shape || !shape.length) {
    //   setInvalidShape(true);
    //   valid = false;
    // } else {
    //   setInvalidShape(false);
    // }
    // if (!length) {
    //   setInvalidLength(true);
    //   valid = false;
    // } else {
    //   setInvalidLength(false);
    // }
    // if (!gauge) {
    //   setInvalidGauge(true);
    //   valid = false;
    // } else {
    //   setInvalidGauge(false);
    // }
    if (valid) {
      let aliasDisplay = alias || '';
      if (boxPressed) {
        aliasDisplay += ' Box-Pressed';
      }
      if (tubo) {
        aliasDisplay += ' Tubo';
      }
      aliasDisplay = aliasDisplay.trim();

      let displayLength = length;
      // console.log('Length:');
      // console.log(`'${length}'`);
      // FIXME Why can't we use === here? Also, can this whole thing be simplified?
      if (length === 'Other') {
        // console.log('Length is other');
        if (otherLength.indexOf('/') !== -1) {
          // console.log('Other length is:');
          // console.log(otherLength);
          const value = otherLength.replace('-', ' ');
          const parts = value.split(' ');
          const int = parts[0];
          const decimalParts = parts[1].split('/');
          const decimal = parseInt(decimalParts[0]) / parseInt(decimalParts[1]);
          // console.log(decimal);
          const finalValue = parseInt(int) + parseFloat(decimal);
          // console.log(finalValue);
          displayLength = finalValue.toFixed(1);
        } else {
          displayLength = otherLength;
        }
      }

      let displayGauge = gauge;
      if (gauge === 'Other') {
        displayGauge = otherGauge;
      }

      const size = `${parseFloat(displayLength).toFixed(1)}"x${displayGauge}`;

      return {
        id: props.vitola?.id,
        cigar_id: props.vitola?.cigar_id,
        alias: aliasDisplay && aliasDisplay.length > 0 ? aliasDisplay : null,
        shape: shape.value,
        size,
        select_shape: shape,
        pending: false,
        image_url: selectedImage,
        image_uri: selectedImage
          ?.replace('https://cdn.boxpressd.com', '')
          .replace('https://cdn.boxpressd.io', '')
          .replace('https://dev-cdn.boxpressd.com', '')
          .replace('https://dev-cdn.boxpressd.io', ''),
      };
    }
    return false;
  };

  const createChip = (id, label, selected, handleSelect) => (
    <Chip
      id={id}
      key={id}
      label={label}
      color={selected ? 'secondary' : 'default'}
      style={{ margin: 8 }}
      onClick={handleSelect}
    />
  );

  const createLengthChipSet = () => (
    <div>
      {lengths.map((l) => {
        if (l !== 'Other') {
          return createChip(l, `${l}"`, length === l, () => setLength(l));
        }
        return createChip(l, l, length === l, () => setLength(l));
      })}
    </div>
  );

  const createGaugeChipSet = () => (
    <div>
      {gauges.map((g) => {
        if (g !== 'Other') {
          return createChip(g, `${g}`, gauge === g, () => setGauge(g));
        }
        return createChip(g, g, gauge === g, () => setGauge(g));
      })}
    </div>
  );

  const onFileDrop = (file) => {
    if (file) {
      console.log(file);
      console.log('Loading file...');
      const fileReader = new FileReader();
      fileReader.onload = (event) => {
        console.log('Loaded file.');
        const dataUrl = event.target.result;
        console.log(dataUrl);
        setSelectedImage(dataUrl);
        setShowMediaCropper(true);
      };
      fileReader.readAsDataURL(file);
    }
  };

  const uploadMedia = (blob, extension) => {
    // setUploadingMedia(true);
    const uuid = uuidv4();
    const cigarHash = props.vitola?.cigar?.hash_id || new Date().getTime();
    const vitolaHash = props.vitola?.hash_id || new Date().getTime();
    const filename = `${cigarHash}-${vitolaHash}.${extension}`;
    uploadToS3(
      blob,
      `cigars/${cigarHash}/vitolas/${vitolaHash}`,
      filename,
      uuid
    )
      .then((res) => {
        console.log('Uploaded to media server!');
        console.log(res.data);
        // setUploadingMedia(false);
        setSelectedImage(res.data.media_url);
      })
      .catch((err) => {
        // setUploadingMedia(false);
        // ErrorLogger.captureException(err);
        console.error(err);
      });
  };

  const renderMediaCropper = () => {
    return (
      <MediaCropper
        src={selectedImage}
        open={showMediaCropper}
        toggle={() => setShowMediaCropper(false)}
        onClose={() => setShowMediaCropper(false)}
        onSave={(croppedCanvas) => {
          console.log('Saving cropped image...');
          croppedCanvas.toBlob((blob) => {
            console.log('Canvas blob');
            console.log(blob);
            const fileReader = new FileReader();
            fileReader.onload = (event) => {
              const dataUrl = event.target.result;
              console.log(dataUrl);
              setSelectedImage(dataUrl);
            };
            fileReader.readAsDataURL(blob);

            const base64 = croppedCanvas.toDataURL('image/jpeg');
            uploadMedia(blob, base64.split(';')[0].split('/')[1]);

            setShowMediaCropper(false);
          });
        }}
      />
    );
  };

  const renderImageFromUrlModal = () => (
    <ImageImportModal
      type="vitolas"
      open={showImageUrlModal}
      onClose={() => setShowImageUrlModal(false)}
      onResult={(media) => {
        console.log('Image imported successfully');
        console.log(media);
        setSelectedImage(media.media_url);
        setShowImageUrlModal(false);
      }}
    />
  );

  return (
    <Modal isOpen={props.open} onClosed={props.onClose}>
      <div className="modal-header">
        <h5 className="modal-title">
          {props.vitola ? Vitola.formattedName(props.vitola) : 'Add Vitola'}
        </h5>
      </div>
      <div className="modal-body md">
        <div>
          <span>Image</span>
          <span
            style={{
              color: 'rgb(211, 169, 102)',
              cursor: 'pointer',
              float: 'right',
            }}
            onClick={() => setShowImageUrlModal(true)}
          >
            Add image from URL
          </span>
        </div>
        <div>
          <Dropzone
            id="vitolaImageFull"
            ref={vitolaImage}
            files={
              selectedImage
                ? [
                    {
                      preview: selectedImage,
                      name: selectedImage.substring(
                        selectedImage.lastIndexOf('/') + 1
                      ),
                      size: 0,
                    },
                  ]
                : undefined
            }
            dropText="Drop vitola image here"
            style={{ padding: 0 }}
            imagePreviewStyle={{
              width: '100%',
            }}
            onDrop={onFileDrop}
          />
          <div style={{ paddingTop: 10 }}>
            <span>Alias (Optional)</span>
            <input
              name="alias"
              className="form-control"
              type="text"
              value={alias}
              onChange={(e) => setAlias(e.target.value)}
              style={{ textTransform: 'capitalize' }}
              placeholder="Example: No. 2"
            />
          </div>
          <div style={{ paddingTop: 10 }}>
            <span>Shape</span>
            <AsyncCreatableSelect
              value={shape}
              placeholder="Search to select"
              onChange={(value) => setShape(value)}
              getOptionLabel={(option: any) => `${option.label}`}
              getOptionValue={(option: any) => `${option.value}`}
              isOptionSelected={(option: any) =>
                shape && shape.value === option.value
              }
              loadOptions={loadShapes}
              defaultOptions={defaultVitolas}
            />
          </div>
          <div style={{ paddingTop: 10 }}>
            <span>Length</span>
            {createLengthChipSet()}
            {length === 'Other' && (
              <input
                value={otherLength}
                className="form-control"
                placeholder="Example: 5.4 or 5 3/8"
                onChange={(e) => setOtherLength(e.target.value)}
              />
            )}
          </div>
          <div style={{ paddingTop: 10 }}>
            <span>Gauge</span>
            {createGaugeChipSet()}
            {gauge === 'Other' && (
              <input
                value={otherGauge}
                className="form-control"
                placeholder="Example: 80"
                onChange={(e) => setOtherGauge(e.target.value)}
              />
            )}
          </div>
          <div style={{ display: 'flex', marginTop: 12 }}>
            <div>
              <FormControlLabel
                control={<Checkbox checked={boxPressed} />}
                onChange={(e) => setBoxPressed(e.target.checked)}
                label="Box-Pressed"
              />
            </div>

            <div>
              <FormControlLabel
                control={<Checkbox checked={tubo} />}
                onChange={(e) => setTubo(e.target.checked)}
                label="Tubo"
              />
            </div>
          </div>
          <div style={{ paddingTop: 10 }}>
            <span>Recognition (Coming Soon)</span>
            <p style={{ fontSize: '0.75rem' }}>
              Manage awards and ratings such as CA top 25
            </p>
            <Button color="light" onClick={() => alert('Coming soon!')}>
              Add Recognition
            </Button>
          </div>
        </div>
      </div>
      <div className="modal-footer">
        <Button color="light" onClick={props.onClose}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={() => {
            if (!shape) {
              alert('Shape is required');
              return;
            }
            if (length === '' || gauge === '') {
              alert('Size is required');
              return;
            }
            if (typeof props.onSave === 'function') {
              const vitola = validate();
              if (vitola) {
                props.onSave(vitola);
                // Reset state
                setAlias('');
                setShape(null);
                setLength('');
                setGauge('');
                if (typeof props.onClose === 'function') {
                  props.onClose();
                }
              }
            }
          }}
          variant="contained"
        >
          {'Set'}
        </Button>
      </div>
      {renderMediaCropper()}
      {renderImageFromUrlModal()}
    </Modal>
  );
};

export default VitolaEditor;
